import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import { AppButton, AppPagination, AppTable, AppTableActionButton } from '@/components/atoms';
import { PatientEditLink } from '@/components/molecules';
import { renderTableCellTruncate } from '@/utils/tableCellTruncate';
import { useTheme } from '@/shared/providers/theme';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { useResetRezeptPrintInvoice } from '@/hooks/main/useResetRezeptPrintInvoice';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import useAppModal from '@/hooks/useAppModal';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils/apiError';
import type { RezeptInvoiceListItem, RezeptInvoiceListPagination } from '@/services/main/rezept';
import RezeptInvoiceEditModal from './RezeptInvoiceEditModal';

export interface RezeptInvoiceListTableProps {
  items: RezeptInvoiceListItem[];
  pagination: RezeptInvoiceListPagination | null;
  /** Cross-page rezept id list — drives the 全 / 反 header buttons. */
  allIds: number[];
  loading?: boolean;
  resetKey?: number;
  selectedIds: number[];
  onChangeSelectedIds: (ids: number[]) => void;
  onChangePage: (page: number, pageSize: number) => void;
  /** 1=月別 (use treatement_first_date), 2=日別 (use searched day). */
  searchTermType?: 1 | 2;
  searchTreatmentOne?: string;
}

/** "202603" | 202603 → "令和08年03月" */
function formatTreatementYearmonth(ym: number | string | null | undefined): string {
  if (ym == null || ym === '') return '';
  const s = String(ym);
  if (s.length !== 6) return s;
  const y = Number(s.slice(0, 4));
  const m = s.slice(4);
  if (y >= 2019) return `令和${String(y - 2018).padStart(2, '0')}年${m}月`;
  if (y >= 1989) return `平成${String(y - 1988).padStart(2, '0')}年${m}月`;
  return `${y}年${m}月`;
}

const numericCompare = (a: unknown, b: unknown) => {
  const an = Number(String(a ?? '').replace(/\D/g, ''));
  const bn = Number(String(b ?? '').replace(/\D/g, ''));
  if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) return an - bn;
  return String(a ?? '').localeCompare(String(b ?? ''));
};

const stringCompare = (a: unknown, b: unknown) => String(a ?? '').localeCompare(String(b ?? ''));

const RezeptInvoiceListTable: React.FC<RezeptInvoiceListTableProps> = ({
  items,
  pagination,
  allIds,
  loading = false,
  resetKey = 0,
  selectedIds,
  onChangeSelectedIds,
  onChangePage,
  searchTermType,
  searchTreatmentOne,
}) => {
  const { serviceType } = useTheme();
  const isR2 = String(serviceType ?? '').toUpperCase() === SERVICE_TYPE_R2;
  const isDaily = searchTermType === 2 && !!searchTreatmentOne;
  const { tenant } = useClinicContext();
  const { confirm } = useAppModal();
  const toast = useAppToast();
  const resetMutation = useResetRezeptPrintInvoice();
  const navigate = useNavigate();

  // 保険外 編集 modal — opened from the 編集 button in the 保険外 column.
  const [editRecord, setEditRecord] = useState<RezeptInvoiceListItem | null>(null);

  // 日別 (search_term_type=2): 訪問入力 lands on the searched day.
  // 月別 (search_term_type=1): land on the week containing the rezept's
  // first treatment date for that month. Fallback: 1st of treatement_yearmonth → today.
  const handleNavigateVisit = useCallback(
    (record: RezeptInvoiceListItem) => {
      if (!tenant || !record.massager_id) return;
      let date: string;
      if (searchTermType === 2 && searchTreatmentOne) {
        date = dayjs(searchTreatmentOne).format('YYYY-MM-DD');
      } else if (record.treatement_first_date) {
        date = dayjs(record.treatement_first_date).format('YYYY-MM-DD');
      } else {
        const ym = String(record.treatement_yearmonth ?? '');
        date = /^\d{6}$/.test(ym)
          ? `${ym.slice(0, 4)}-${ym.slice(4)}-01`
          : dayjs().format('YYYY-MM-DD');
      }
      navigate(`/${tenant}/visits/schedule/${record.massager_id}/${date}`);
    },
    [tenant, searchTermType, searchTreatmentOne, navigate]
  );

  const handleResetPrintInvoice = useCallback(
    (id: number) => {
      confirm({
        title: '印刷済みをリセットしますか？',
        okText: 'OK',
        cancelText: 'キャンセル',
        onOk: () =>
          resetMutation.mutateAsync(id).catch(err => {
            toast.error(getApiErrorMessage(err, 'リセットに失敗しました。'));
          }),
      });
    },
    [confirm, resetMutation, toast]
  );

  // Toggle one row's selection — used by row click and (indirectly) the
  // checkbox column. Click-anywhere-on-row affects ONLY that row, never
  // bleeds across rows of the same patient (BD requirement).
  const toggleRowSelection = useCallback(
    (id: number) => {
      onChangeSelectedIds(
        selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]
      );
    },
    [selectedIds, onChangeSelectedIds]
  );

  // 「全」: select-all toggle that operates across ALL pages of the search
  // result. If every id is already selected → unselect all; otherwise → select
  // every id in `allIds`.
  const handleSelectAllToggle = useCallback(() => {
    if (allIds.length === 0) return;
    const selectedSet = new Set(selectedIds);
    const allSelected = allIds.every(id => selectedSet.has(id));
    onChangeSelectedIds(allSelected ? [] : [...allIds]);
  }, [allIds, selectedIds, onChangeSelectedIds]);

  // 「反」: invert each id's selected state across all pages.
  const handleInvertSelection = useCallback(() => {
    const selectedSet = new Set(selectedIds);
    onChangeSelectedIds(allIds.filter(id => !selectedSet.has(id)));
  }, [allIds, selectedIds, onChangeSelectedIds]);

  const columns = useMemo<ColumnsType<RezeptInvoiceListItem>>(() => {
    const common: ColumnsType<RezeptInvoiceListItem> = [
      {
        title: '受診者番号',
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: 100,
        sorter: (a, b) => numericCompare(a.serial_number, b.serial_number),
        render: (value: string | number, record: RezeptInvoiceListItem) => (
          <PatientEditLink
            patientId={record.patient_id}
            value={value}
            stopPropagation
            ariaLabel="受診者情報"
          />
        ),
      },
      {
        title: '受診者名',
        dataIndex: 'patient_name',
        key: 'patient_name',
        width: 300,
        align: 'left',
        onHeaderCell: () => ({ style: { textAlign: 'center' as const } }),
        sorter: (a, b) => stringCompare(a.patient_name, b.patient_name),
        render: (v: string | null) => renderTableCellTruncate(v),
      },
      {
        title: '保険者番号',
        dataIndex: 'insurer_number',
        key: 'insurer_number',
        width: 110,
        align: 'left',
        onHeaderCell: () => ({ style: { textAlign: 'center' as const } }),
        sorter: (a, b) => numericCompare(a.insurer_number, b.insurer_number),
      },
      {
        title: '保険者名',
        dataIndex: 'insurer_name',
        key: 'insurer_name',
        width: 300,
        align: 'left',
        onHeaderCell: () => ({ style: { textAlign: 'center' as const } }),
        sorter: (a, b) => stringCompare(a.insurer_name, b.insurer_name),
        render: (v: string | null) => renderTableCellTruncate(v),
      },
      {
        title: '施術年月',
        dataIndex: 'treatement_yearmonth',
        key: 'treatement_yearmonth',
        width: 120,
        align: 'left',
        onHeaderCell: () => ({ style: { textAlign: 'center' as const } }),
        render: (v: number | string) => formatTreatementYearmonth(v),
      },
    ];

    const visitButton = (record: RezeptInvoiceListItem) => (
      <AppTableActionButton
        action="edit"
        disabled={!record.massager_id}
        onClick={e => {
          e.stopPropagation();
          handleNavigateVisit(record);
        }}
      >
        訪問
      </AppTableActionButton>
    );

    const printColumn: ColumnsType<RezeptInvoiceListItem>[number] = {
      title: '印刷',
      dataIndex: 'print_invoice',
      key: 'print_invoice',
      width: 80,
      align: 'center',
      render: (v: number | null, record: RezeptInvoiceListItem) =>
        v != null && v !== -1 ? (
          <AppButton
            type="link"
            className="!p-0 !h-auto text-blue-600 underline"
            onClick={e => {
              e.stopPropagation();
              handleResetPrintInvoice(record.id);
            }}
          >
            済
          </AppButton>
        ) : null,
    };

    // 操作 (訪問 button) — shown in both A2 and R2.
    const actionColumn: ColumnsType<RezeptInvoiceListItem>[number] = {
      title: '操作',
      key: 'action',
      width: 70,
      align: 'center',
      render: (_: unknown, record: RezeptInvoiceListItem) => visitButton(record),
    };

    // HIT-7340: 保険外 is registered per treatment day, so 月別 has no 保険外 column
    // (its receipt prints the sum of the days).
    if (isR2 || !isDaily) {
      // R2 / A2 月別: 印刷 + 操作 (訪問 button). No 保険外 column.
      return [...common, printColumn, actionColumn];
    }

    // A2 日別: 印刷 + 保険外 (○) + 操作 (訪問 button).
    return [
      ...common,
      printColumn,
      {
        title: '保険外',
        dataIndex: 'free_medical_treatment_name',
        key: 'free_medical_treatment_name',
        width: 90,
        onHeaderCell: () => ({ style: { textAlign: 'center' as const } }),
        render: (v: string | null, record: RezeptInvoiceListItem) => (
          <div className="flex items-center justify-between">
            <span>{v ? '〇' : ''}</span>
            <AppButton
              className="!px-[13px]"
              type="primary"
              onClick={e => {
                e.stopPropagation();
                setEditRecord(record);
              }}
            >
              編集
            </AppButton>
          </div>
        ),
      },
      actionColumn,
    ];
  }, [isR2, isDaily, handleResetPrintInvoice, handleNavigateVisit]);

  return (
    <div className="space-y-3">
      <AppTable<RezeptInvoiceListItem>
        rowKey="id"
        dataSource={items}
        columns={columns}
        recordCount={pagination?.total ?? items.length}
        loading={loading}
        key={`${resetKey}-${pagination?.current_page ?? 1}-${pagination?.per_page ?? items.length}`}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: keys => onChangeSelectedIds(keys.map(k => Number(k))),
          columnWidth: 70,
          columnTitle: (
            <div
              className="flex items-center justify-center gap-1 -my-1"
              onClick={e => e.stopPropagation()}
            >
              <AppButton
                id="all_rezept_ids"
                type="default"
                size="small"
                onClick={handleSelectAllToggle}
                className="!px-1.5 !py-0 !h-6 !leading-none text-xs"
              >
                全
              </AppButton>
              <AppButton
                id="inversion_rezept_ids"
                type="default"
                size="small"
                onClick={handleInvertSelection}
                className="!px-1.5 !py-0 !h-6 !leading-none text-xs"
              >
                反
              </AppButton>
            </div>
          ),
        }}
        // BD: clicking anywhere on a row (except the checkbox / interactive
        // children that already stopPropagation) toggles that row's checkbox.
        onRow={record => ({
          onClick: () => toggleRowSelection(record.id),
          style: { cursor: 'pointer' },
        })}
        pagination={false}
      />
      {pagination && (
        <AppPagination
          current={pagination.current_page}
          pageSize={pagination.per_page}
          total={pagination.total}
          onChange={(page, pageSize) => onChangePage(page, pageSize)}
        />
      )}
      <RezeptInvoiceEditModal
        isOpen={editRecord !== null}
        onClose={() => setEditRecord(null)}
        record={editRecord}
        visitDate={isDaily ? dayjs(searchTreatmentOne).format('YYYY-MM-DD') : null}
      />
    </div>
  );
};

export default RezeptInvoiceListTable;
